import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import BrandLogo from "@/components/BrandLogo";
import {
  Home, Target, MessageCircle, GitCompareArrows, Building2, Upload, KeyRound,
  ChevronLeft, ChevronRight, X, Filter, Wallet, LogOut, UserPlus, Sparkles, Activity, Database, SlidersHorizontal, ListChecks,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/components/AuthContext";
import { useNavigate } from "react-router-dom";

const NAV_SECTIONS = [
  {
    title: "數據中心",
    items: [
      { to: "/", label: "總覽", testid: "nav-home", Icon: Home, end: true, alertKey: "home" },
      { to: "/meta-poc", label: "Meta 廣告", testid: "nav-link-meta-poc", Icon: Target },
      { to: "/line-insights", label: "LINE 洞察", testid: "nav-link-line-insights", Icon: MessageCircle },
      { to: "/funnel", label: "漏斗分析", testid: "nav-link-funnel", Icon: Filter, alertKey: "funnel" },
      { to: "/compare", label: "跨域對比", testid: "nav-link-compare", Icon: GitCompareArrows },
      { to: "/advisor", label: "包棟大師AI顧問", testid: "nav-link-advisor", Icon: Sparkles },
    ],
  },
  {
    title: "場域與匯入",
    items: [
      { to: "/settings/properties", label: "場域管理", testid: "nav-settings-properties", Icon: Building2 },
      { to: "/line-poc", label: "LINE 歷史對話分析", testid: "nav-link-line-poc", Icon: Upload },
      { to: "/line-classification-review", label: "分類核對", testid: "nav-link-classification-review", Icon: ListChecks },
    ],
  },
  {
    title: "設定",
    items: [
      { to: "/settings", label: "Meta 設定", testid: "nav-settings-meta", Icon: KeyRound, end: true },
      { to: "/settings/line-sources", label: "加好友管道", testid: "nav-settings-line-sources", Icon: UserPlus },
      { to: "/settings/integrations/pms", label: "PMS 整合", testid: "nav-settings-pms", Icon: Database },
      { to: "/settings/data-health", label: "資料健康檢查", testid: "nav-settings-data-health", Icon: Activity },
      { to: "/settings/revenue", label: "營收 API", testid: "nav-settings-revenue", Icon: Wallet },
      { to: "/settings/rules", label: "異常與燈號規則", testid: "nav-settings-rules", Icon: SlidersHorizontal },
    ],
  },
];

export const PAGE_TITLES = {
  "/": "總覽",
  "/meta-poc": "Meta 廣告",
  "/line-insights": "LINE 洞察",
  "/funnel": "漏斗分析",
  "/compare": "跨域對比",
  "/settings": "Meta 設定",
  "/settings/properties": "場域管理",
  "/settings/revenue": "營收 API",
  "/settings/line-sources": "加好友管道分析",
  "/settings/integrations/pms": "PMS 整合",
  "/settings/data-health": "資料健康檢查",
  "/settings/rules": "異常與燈號規則",
  "/line-poc": "LINE 歷史對話分析",
  "/line-classification-review": "分類核對",
  "/advisor": "包棟大師AI顧問",
};

function Item({ item, collapsed, alertCount = 0 }) {
  // H1 — collapsed (icon-only) sidebar: label in the shared on-top tooltip instead of native title
  if (collapsed) {
    return (
      <TooltipProvider delayDuration={150}><Tooltip>
        <TooltipTrigger asChild><span className="block"><ItemLink item={item} collapsed alertCount={alertCount} /></span></TooltipTrigger>
        <TooltipContent side="right" className="bg-slate-900 text-white" data-testid={`nav-tip-${item.testid || item.to.replace(/\W+/g, "-")}`}>{item.label}{alertCount > 0 ? `（${alertCount} 條警示）` : ""}</TooltipContent>
      </Tooltip></TooltipProvider>
    );
  }
  return <ItemLink item={item} collapsed={false} alertCount={alertCount} />;
}

function ItemLink({ item, collapsed, alertCount = 0 }) {
  const Icon = item.Icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.testid}
      className={({ isActive }) => [
        "relative flex items-center gap-3 rounded-full px-3 py-2 text-sm font-medium transition-colors",
        "mx-2 my-0.5",
        isActive
          ? "bg-[#FEF3E8] text-[#C04C0F]"
          : "text-slate-500 hover:bg-slate-100 hover:text-slate-800",
        collapsed ? "justify-center px-2" : "",
      ].join(" ")}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-[#F26A1F]" aria-hidden />
          )}
          <div className="relative flex-shrink-0">
            <Icon className={`h-[18px] w-[18px] ${isActive ? "text-[#F26A1F]" : ""}`} />
            {collapsed && alertCount > 0 && (
              <span
                className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-semibold leading-[16px] text-center ring-2 ring-white"
                aria-label={`${alertCount} 條警示`}
                data-testid={`nav-alert-badge-${item.testid}`}
              >
                {alertCount > 99 ? "99+" : alertCount}
              </span>
            )}
          </div>
          {!collapsed && (
            <span className="truncate inline-flex items-center gap-1.5 flex-1 min-w-0">
              <span className="truncate">{item.label}</span>
              {alertCount > 0 && (
                <span
                  className="ml-auto min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold leading-[18px] text-center tabular-nums"
                  aria-label={`${alertCount} 條警示`}
                  data-testid={`nav-alert-badge-${item.testid}`}
                >
                  {alertCount > 99 ? "99+" : alertCount}
                </span>
              )}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function AppSidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { logout } = useAuth();
  const [alertCount, setAlertCount] = useState(0);
  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname, setMobileOpen]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/alerts/summary");
        if (!cancelled) setAlertCount(r.data.total_alerts || 0);
      } catch (e) { console.error("alerts summary fetch failed:", e); }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [loc.pathname]);

  const content = (
    <>
      <div className={`flex items-center ${collapsed ? "flex-col justify-center gap-1 px-2" : "justify-between px-4"} h-11 border-b border-slate-200`}>
        {collapsed ? (
          <BrandLogo size="sm" showChinese={false} />
        ) : (
          <BrandLogo size="sm" />
        )}
        <button
          data-testid="sidebar-collapse-toggle"
          onClick={() => setCollapsed((c) => !c)}
          className="hidden md:inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-slate-100 text-slate-500"
          aria-label={collapsed ? "展開側邊欄" : "收合側邊欄"}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
        <button
          data-testid="sidebar-mobile-close"
          onClick={() => setMobileOpen(false)}
          className="inline-flex md:hidden h-7 w-7 items-center justify-center rounded-md hover:bg-slate-100 text-slate-500"
          aria-label="關閉"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3" data-testid="app-sidebar-nav">
        {NAV_SECTIONS.map((sec) => (
          <div key={sec.title} className="mb-3">
            {!collapsed && (
              <div className="px-5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                {sec.title}
              </div>
            )}
            {sec.items.map((it) => <Item key={it.to} item={it} collapsed={collapsed} alertCount={it.alertKey ? alertCount : 0} />)}
          </div>
        ))}
      </nav>

      <div className={`border-t border-slate-200 ${collapsed ? "px-2" : "px-3"} py-3 space-y-2`}>
        <button
          onClick={async () => { await logout(); nav("/login", { replace: true }); }}
          title={collapsed ? "登出" : undefined}
          data-testid="sidebar-logout-btn"
          className={`${collapsed ? "justify-center w-full" : "w-full justify-start"} inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-medium text-slate-500 hover:bg-rose-50 hover:text-rose-700 transition-colors`}
        >
          <LogOut className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span>登出</span>}
        </button>
        {!collapsed && (
          <div className="text-[10px] text-slate-400 px-2">
            v B.2 · 人客多行銷數據中心
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop */}
      <aside
        data-testid="app-sidebar"
        className={`hidden md:flex flex-col fixed top-0 left-0 h-screen bg-white border-r border-slate-200 shadow-sm z-30 transition-all ${collapsed ? "w-[64px]" : "w-[220px]"}`}
      >
        {content}
      </aside>
      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="app-sidebar-drawer">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative flex flex-col w-[260px] max-w-[80vw] bg-white shadow-xl">
            {content}
          </aside>
        </div>
      )}
    </>
  );
}
